import { Lightning, Router } from '@lightningjs/sdk'

import Button from '../components/button/button'
import { getColor, grey200, grey960, white } from '../constants/colors'
import { UniNeueBold, UniNeueRegular } from '../constants/fonts'
import { getText } from '../utils/dictionary'
import { captureScreenData, NON_BFF_SEGMENT_SCREENS } from '../utils/segmentAnalytics'

export interface ModalParams {
  title?: string
  info?: string
  footer?: string
  buttons: ModalButton[]
  wordWrap?: boolean
  isBFFDriven?: boolean
}

interface ModalButton {
  label: string
  callback?: () => void
  isDefaultBackAction?: boolean
}

export const networkError = {
  title: getText('connectionErrorTittle'),
  info: getText('connectionErrorSubtitle'),
  isBFFDriven: true,
  buttons: [
    {
      label: getText('reload'),
      callback: () => {
        Router.reload()
      },
    },
  ],
} as ModalParams

export const genericError = {
  title: getText('genericErrorTitle'),
  info: getText('genericErrorText'),
  isBFFDriven: true,
  buttons: [
    {
      label: getText('reload'),
      callback: () => {
        Router.reload()
      },
    },
  ],
} as ModalParams

export default class Modal extends Lightning.Component {
  private _curBtnIndex: number = 0
  private _buttons: ModalButton[] = []
  private _retryAction: () => void
  private _isBffDriven = false

  static _template() {
    return {
      w: 1920,
      h: 1080,
      rect: true,
      color: getColor(grey960),
      collision: true,
      flex: {
        direction: 'column',
        alignItems: 'center',
        justifyContent: 'center',
      },
      Title: {
        text: {
          text: getText('genericErrorTitle'),
          textColor: getColor(white),
          fontFace: UniNeueBold,
          fontSize: 48,
          lineHeight: 56,
          wordWrapWidth: 720,
        },
      },
      Info: {
        flexItem: {
          marginBottom: 44,
        },
        text: {
          text: getText('genericErrorText'),
          textColor: getColor(grey200),
          fontFace: UniNeueRegular,
          fontSize: 40,
          textAlign: 'center',
          lineHeight: 48,
          wordWrapWidth: 890,
        },
      },
      Buttons: {
        flex: {
          direction: 'column',
          alignItems: 'center',
          justifyContent: 'center',
        },
      },
      Footer: {
        visible: false,
        flexItem: {
          marginTop: 204,
        },
        text: {
          text: '',
          textColor: getColor(grey200),
          fontFace: UniNeueRegular,
          fontSize: 28,
          textAlign: 'center',
          lineHeight: 36,
          wordWrapWidth: 430,
        },
      },
    }
  }

  get title() {
    return this.tag('Title')
  }

  get info() {
    return this.tag('Info')
  }

  get footer() {
    return this.tag('Footer')
  }

  set buttonsContainer(v: ModalButton[]) {
    this._buttons = v
    const btns = v.map((b, i) => {
      if (b.isDefaultBackAction) {
        this._retryAction = b.callback
      }

      return {
        type: Button,
        w: 668,
        label: b.label,
        flexItem: {
          marginTop: i > 0 ? 24 : 0,
        },
        buttonIndex: i,
        zIndex: this.zIndex + 1,
      }
    })

    this.buttons.patch({ children: btns })
  }

  get buttons() {
    return this.tag('Buttons')
  }

  get currentButton() {
    return this.buttons.children[this._curBtnIndex]
  }

  set data(v: ModalParams) {
    this.title.patch({
      text: {
        text: v.title ? v.title : getText('genericErrorTitle'),
        wordWrapWidth: v?.wordWrap ? 720 : 0,
      },
    })

    this.info.patch({
      text: {
        text: v.info ? v.info : getText('genericErrorText'),
        wordWrapWidth: v?.wordWrap ? 890 : 0,
      },
    })

    if (v?.footer) {
      this.footer.visible = true
      this.footer.patch({ text: { text: v.footer } })
    } else {
      this.footer.visible = false
    }

    if (v?.buttons) {
      this.buttonsContainer = v.buttons
    }
  }

  get page() {
    return Router.getActivePage()
  }

  _getFocused() {
    return this.currentButton
  }

  _focus() {
    this.visible = true
    void captureScreenData(NON_BFF_SEGMENT_SCREENS.Error, this.title.text?.text)
  }

  _inactive() {
    this._curBtnIndex = 0
  }

  _handleEnter() {
    const cb = this._buttons[this._curBtnIndex]?.callback
    if (cb) {
      cb()
    } else {
      this._handleBack()
    }
  }

  _handleBack() {
    if (Router.getActivePage().widgets.menu.visible) {
      Router.focusWidget('Menu')
      return
    } else {
      if (this._isBffDriven) {
        Router.back()
      }
    }

    this.invokeRetryAction()
  }

  invokeRetryAction() {
    const currentPage = Router.getActivePage()

    if (currentPage.ref === 'Player') {
      ;(this.page as any)._setState('Playback')
    } else {
      ;(this.page as any)._setState('Ready')
    }

    Router.focusPage()

    if (this._retryAction) {
      this._retryAction()
    } else {
      // If we got here means there was no action hiding the modal so we hide it
      Router.getActivePage().widgets.modal.visible = false
    }
  }

  _handleKey() {
    // Prevent modal from trying to focus somewhere else
    return true
  }

  _handleLeft() {
    if (Router.getActivePage().widgets.menu.visible) {
      Router.focusWidget('Menu')
    }
  }

  _handleUp() {
    if (this._curBtnIndex > 0) {
      this._curBtnIndex--
    }
  }

  _handleDown() {
    if (this._curBtnIndex < this._buttons.length - 1) {
      this._curBtnIndex++
    }
  }

  _handleCursorHide() {
    // Allows capturing the event when the cursor disappears by being idle
    // triggering this modal's _getFocused() method
    this._refocus()
    ;(this.currentButton as Button)?._focus()
  }

  $updateFocusByHover({ buttonIndex }) {
    this._curBtnIndex = buttonIndex
    this._refocus()
  }

  _handleClick(element) {
    if (element instanceof Button) {
      this._handleEnter()
    }
  }
}
